<template>
  <v-data-table-server
    :headers="headers"
    :items="items"
    :items-length="itemsLength"
    :loading="loading"
    :sort-by="sortBy"
    :item-value="itemValue"
    :show-select="showSelect"
    :model-value="modelValue"
    :color="color"
    :row-props="rowProps"
    :expanded="expanded"
    :show-expand="showExpand"
    density="comfortable"
    hover
    hide-default-footer
    :must-sort="mustSort"
    @update:sort-by="$emit('update:sort-by', $event)"
    @update:model-value="$emit('update:model-value', $event)"
    @update:expanded="$emit('update:expanded', $event)"
  >
    <template v-for="slotName in Object.keys($slots)" :key="slotName" #[slotName]="scope">
      <!-- Vuetify invokes some slots (loading/no-data) with zero arguments,
           leaving `scope` undefined. The compiler wraps v-bind targets in
           guardReactiveProps(), which turns undefined into null - and
           renderSlot()'s own default parameter only covers undefined, not
           null - so forwarding raw `scope` throws "Cannot read properties
           of null (reading 'key')" the first time such a slot renders. The
           `|| {}` keeps that guard from ever seeing undefined. -->
      <slot :name="slotName" v-bind="scope || {}" />
    </template>
  </v-data-table-server>
</template>

<script setup lang="ts">
import type { PropType } from 'vue';
import type { SortByItem } from '@/models/llDataTableTypes';

// Reusable table shell shared across list pages (Matters is the first
// consumer; Fixed Fees/Time Entries can adopt it later without a rewrite).
// It owns only headers/items/loading/sort/selection wiring - column
// rendering, row actions, loading/empty states, and pagination placement
// all stay with the consuming page via slots and the separate
// LlDataTablePagination component, since those vary too much per page to
// bake into one shared shape.
//
// `fixed-header` is intentionally not a default here: it only has a visual
// effect when the table is also given a bounded height, which needs to be
// solved per-page against that page's actual layout (see MattersTable.vue's
// history with the app shell's real scroll container before reaching for
// this).
//
// This deliberately uses v-data-table-server, not plain v-data-table. Every
// consumer here does its own server-side sort/pagination, and plain
// v-data-table still re-sorts and re-paginates whatever `items` it's given
// on top of that (matching header.value against each item, client-side) -
// harmless for columns where the displayed field is also the sort key, but
// it silently produces the wrong order for any column where they differ
// (e.g. Matters' Responsible column sorts by initials server-side but
// displays/would compare full names client-side). v-data-table-server
// trusts `items`' given order completely and never re-sorts/re-paginates it.

defineProps({
  headers: { type: Array, required: true },
  items: { type: Array, required: true },
  itemsLength: { type: [Number, String], required: true },
  loading: { type: Boolean, default: false },
  sortBy: { type: Array as PropType<SortByItem[]>, default: (): SortByItem[] => [] },
  itemValue: { type: String, default: 'id' },
  mustSort: { type: Boolean, default: true },
  showSelect: { type: Boolean, default: false },
  modelValue: { type: Array, default: undefined },
  // Colors the selection checkboxes (and selected-row tint) - defaults to the
  // theme's primary so every consumer matches without having to set it
  // themselves; override only if a specific page needs to differ. Vuetify
  // passes this to each checkbox explicitly, so the VCheckboxBtn default in
  // vuetifyTheme.ts never reaches them.
  color: { type: String, default: 'primary' },
  // Per-row attributes, as Vuetify's row-props: either an object applied to
  // every row, or a ({ item, index }) => object for row-dependent styling.
  // Pages that tint rows by state (Track Sent Emails shades paid invoices)
  // need this; without it they'd have to take over the whole `item` slot and
  // lose show-select's built-in selection handling.
  rowProps: { type: [Object, Function], default: undefined },
  // Item values of the currently expanded rows, paired with the `expanded-row`
  // slot. `showExpand` only controls whether Vuetify renders its own chevron
  // column - a page that drives expansion from its own cells (Track Sent
  // Emails toggles from the Type and Recipients cells, as it always has) binds
  // `expanded` and leaves `showExpand` off so the column set is unchanged.
  expanded: { type: Array, default: () => [] },
  showExpand: { type: Boolean, default: false },
});

defineEmits(['update:sort-by', 'update:model-value', 'update:expanded']);
</script>
